import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PeerPlayer from './PeerPlayer';
import * as PublishSettingsActions from '../../actions/publishSettingsActions';
import * as WebRTCMeetingActions from '../../actions/webrtcMeetingActions';
import * as ErrorsActions from '../../actions/errorsActions';
import startPublish from '../../webrtc/startPublish';
import stopPublish from '../../webrtc/stopPublish';

const Hopper = () => {
  const dispatch = useDispatch();
  const publishSettings = useSelector((state) => state.publishSettings);
  const compositeSettings = useSelector((state) => state.compositeSettings);
  const { connected, websocket, peerConnection, peerStreams } = useSelector((state) => state.webrtcMeeting);
  const peerStreamsRef = useRef(peerStreams);

  useEffect(() => {
    peerStreamsRef.current = peerStreams;
  });

  useEffect(() => {
    // Use the compositor tracks for publishing
    // This ensures the stream sent to Wowza is the one from the canvas.
    dispatch({type:PublishSettingsActions.SET_PUBLISH_VIDEO_TRACK, videoTrack:compositeSettings.videoTrack});
    dispatch({type:PublishSettingsActions.SET_PUBLISH_AUDIO_TRACK, audioTrack:compositeSettings.audioTrack});
  }, [dispatch, compositeSettings.videoTrack, compositeSettings.audioTrack]);


  useEffect(() => {
    if (publishSettings.publishStart && !publishSettings.publishStarting && !connected) {
      // update flags
      dispatch({type:PublishSettingsActions.SET_PUBLISH_FLAGS, publishStart:false, publishStarting:true});
      // Start publishing to Wowza Streaming Engine
      startPublish(publishSettings,websocket,{
        onError: (error) => {
          dispatch(PublishSettingsActions.resetPublish())
          dispatch({type:ErrorsActions.SET_ERROR_MESSAGE,message:error.message});
        },
        onSetPeerConnection: (result) => {
          dispatch({type:WebRTCMeetingActions.SET_WEBRTC_MEETING_PEERCONNECTION,peerConnection:result.peerConnection});
        },
        onSetWebsocket: (result) => {
          dispatch({type:WebRTCMeetingActions.SET_WEBRTC_MEETING_WEBSOCKET,websocket:result.websocket});
        },
        onConnectionStateChange: (result) => {
          dispatch({type:WebRTCMeetingActions.SET_WEBRTC_MEETING_CONNECTED, connected:result.connected});
        }
      });
    } else if (publishSettings.publishStarting && connected){
      dispatch({type:PublishSettingsActions.SET_PUBLISH_FLAGS, publishStarting:false});
      // insert peer player with cam and mic as source
      dispatch({type:WebRTCMeetingActions.ADD_PEER_VIDEO_PLAYER,peerStream:{streamName: publishSettings.streamName}})
    } else if (publishSettings.publishStop && !publishSettings.publishStopping && connected) {
      dispatch({type:PublishSettingsActions.SET_PUBLISH_FLAGS, publishStop:false, publishStopping:true});
      dispatch({type:WebRTCMeetingActions.REMOVE_ALL_PLAYERS})
      stopPublish(peerConnection,websocket,{
        onSetPeerConnection: (result) => {
          dispatch({type:WebRTCMeetingActions.SET_WEBRTC_MEETING_PEERCONNECTION,peerConnection:result.peerConnection});
        },
        onSetWebsocket: (result) => {
          dispatch({type:WebRTCMeetingActions.SET_WEBRTC_MEETING_WEBSOCKET,websocket:result.websocket});
        },
        onPublishStopped: () => {
          dispatch({type:WebRTCMeetingActions.SET_WEBRTC_MEETING_CONNECTED, connected:false});
        }
      });
    } else if (publishSettings.publishStopping && !connected) {
      dispatch({type:PublishSettingsActions.SET_PUBLISH_FLAGS, publishStopping:false});
    }
  }, [dispatch, publishSettings, connected, websocket, peerConnection]);

  useEffect(() => {
    if (!connected || !websocket) return;

    const handleMessage = (event) => {
      let msgJSON = JSON.parse(event.data);
      let msgCommand = msgJSON['command'];
      if ('getAvailableStreams'.localeCompare(msgCommand) === 0) {
        if(msgJSON["availableStreams"] !== null){
          let availableStreams = msgJSON["availableStreams"].filter((stream) => !(stream.streamName.includes(publishSettings.streamName) && stream.streamName.includes("_")))
          //  filter out this stream from the playback ones since we have a dedicated player for it
          availableStreams = availableStreams.filter((stream) => !(stream.streamName === publishSettings.streamName))

          // Check for new streams to add
          for(let i=0;i<availableStreams.length;i++){
            WebRTCMeetingActions.checkAddPeerStream(peerStreamsRef.current, availableStreams[i].streamName).then((action) => {
              if (action) dispatch(action);
            });
          }
          // Check for streams to remove
          for(let i=0;i<peerStreamsRef.current.length;i++){
            if(!(peerStreamsRef.current[i].streamName === publishSettings.streamName)){
              WebRTCMeetingActions.checkRemovePeerStream(availableStreams,peerStreamsRef.current[i].streamName).then((action) => {
                if (action) dispatch(action);
              });
            }
          }
        }
      }
    };

    websocket.addEventListener("message", handleMessage);

    const pollingInterval = setInterval(() => {
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        let streamInfo = {
          applicationName:publishSettings.applicationName,
          streamName:publishSettings.streamName,
          sessionId:"[empty]"
        };
        websocket.send('{"direction":"publish", "command":"getAvailableStreams", "streamInfo":' + JSON.stringify(streamInfo) + '}');
      }
    }, 3000);

    return () => {
      if (websocket) {
        websocket.removeEventListener("message", handleMessage);
      }
    };
  }, [dispatch, connected, websocket, publishSettings.streamName]);

  return (
    <div id="player-list">
      {peerStreams.map((peerStream, key) => (
        <PeerPlayer streamName={peerStream.streamName} key={key} />
      ))}
    </div>
  );
};

export default Hopper;
